import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";

/* ── Nav links ── */
const links = [
  { to: "/", label: "Home" },
  { to: "/vision", label: "Vision" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      {/* ════════ NAVBAR ════════ */}
      <nav
        className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-500 bg-black ${scrolled
          ? "h-12 xs:h-14 sm:h-16 shadow-lg shadow-black/30"
          : "h-14 xs:h-16 sm:h-[72px]"
          }`}
      >
        {/* Gold accent line at top */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600" />

        <div className="h-full mx-auto px-4 sm:px-6 md:px-8 lg:px-14 flex items-center justify-between max-w-7xl">

          {/* ── Logo ── */}
          <div
            className="flex items-center gap-2 sm:gap-3 md:gap-4 cursor-pointer group min-w-0"
            onClick={() => { setOpen(false); navigate("/"); }}
          >
            <img
              src="/assets/logo.png"
              alt="The Lutyens Project"
              className={`w-auto object-contain flex-shrink-0 transition-all duration-500 ${scrolled ? "h-7 sm:h-9 md:h-10" : "h-8 sm:h-10 md:h-12"
                }`}
            />
            <div className="h-6 sm:h-8 w-px bg-amber-700/40 hidden sm:block flex-shrink-0" />
            <div className="flex flex-col min-w-0">
              <span
                className="text-white font-bold text-[13px] sm:text-[15px] md:text-lg leading-tight tracking-tight truncate"
                style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
              >
                The Lutyens
              </span>
              <span className="text-amber-500/60 text-[6px] sm:text-[7px] md:text-[8px] font-bold tracking-[0.3em] sm:tracking-[0.35em] uppercase leading-none mt-0.5">
                Project
              </span>
            </div>
          </div>

          {/* ── Desktop Links ── */}
          <div className="hidden md:flex items-center gap-0.5 bg-white/[0.04] rounded-full px-1 py-1 border border-white/[0.06]">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `relative px-6 py-2 text-[13px] font-semibold tracking-wide rounded-full transition-all duration-300 ${isActive
                    ? "text-gray-900 bg-gradient-to-r from-amber-400 to-yellow-400 shadow-md shadow-amber-500/20"
                    : "text-white/60 hover:text-white"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          {/* ── Hamburger ── */}
          <button
            className="md:hidden relative w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full hover:bg-white/[0.06] transition-colors duration-200 flex-shrink-0"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            <div className="w-[18px] flex flex-col items-end gap-[5px]">
              <span className={`h-[2px] bg-white rounded-full transition-all duration-300 ${open ? "w-[18px] translate-y-[7px] rotate-45" : "w-[18px]"
                }`} />
              <span className={`h-[2px] bg-amber-400 rounded-full transition-all duration-300 ${open ? "w-0 opacity-0" : "w-[12px] opacity-100"
                }`} />
              <span className={`h-[2px] bg-white rounded-full transition-all duration-300 ${open ? "w-[18px] -translate-y-[7px] -rotate-45" : "w-[16px]"
                }`} />
            </div>
          </button>
        </div>
      </nav>

      {/* Spacer — matches nav height */}
      <div className={`transition-all duration-500 ${scrolled ? "h-12 xs:h-14 sm:h-16" : "h-14 xs:h-16 sm:h-[72px]"
        }`} />

      {/* ════════ FULL-SCREEN MOBILE MENU ════════ */}
      <div
        className={`fixed inset-0 z-[999] md:hidden flex flex-col transition-all duration-500 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
        style={{ background: "#000" }}
      >
        {/* Top accent */}
        <div className="h-[2px] bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600" />

        {/* Close button — top right, always accessible */}
        <div className="flex justify-end px-4 pt-3 sm:px-6 sm:pt-4">
          <button
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="w-10 h-10 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/[0.06] transition-all duration-200"
            style={{
              transitionDelay: open ? "50ms" : "0ms",
              opacity: open ? 1 : 0,
              transform: open ? "scale(1) rotate(0deg)" : "scale(0.5) rotate(-90deg)",
              transition: "all 0.4s ease-out",
            }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Decorative corner accents */}
        <div className="absolute bottom-8 left-5 sm:bottom-10 sm:left-6 w-16 h-16 sm:w-20 sm:h-20 border-l-2 border-b-2 border-amber-700/20 rounded-bl-2xl pointer-events-none" />
        <div className="absolute top-20 right-5 sm:top-24 sm:right-6 w-12 h-12 sm:w-16 sm:h-16 border-r-2 border-t-2 border-amber-700/20 rounded-tr-2xl pointer-events-none" />

        {/* Center content */}
        <div className="flex-1 flex flex-col items-center justify-center gap-3 sm:gap-4 px-8 sm:px-10">
          {/* Logo in menu */}
          <div
            className="mb-6 sm:mb-8 flex flex-col items-center"
            style={{
              transitionDelay: open ? "60ms" : "0ms",
              transform: open ? "translateY(0)" : "translateY(20px)",
              opacity: open ? 1 : 0,
              transition: "all 0.5s ease-out",
            }}
          >
            <img
              src="/assets/logo.png"
              alt="Logo"
              className="h-12 sm:h-14 w-auto object-contain mb-2"
            />
            <div className="w-8 h-px bg-amber-700/40" />
          </div>

          {links.map((link, i) => (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `w-full max-w-[280px] sm:max-w-xs text-center py-3.5 sm:py-4 rounded-xl sm:rounded-2xl text-lg sm:text-xl font-bold tracking-wide transition-all duration-500 ${isActive
                  ? "text-gray-900 bg-gradient-to-r from-amber-400 to-yellow-400 shadow-lg shadow-amber-500/15"
                  : "text-white/70 hover:text-white border border-white/[0.08] hover:border-white/[0.15]"
                }`
              }
              style={{
                transitionDelay: open ? `${i * 80 + 120}ms` : "0ms",
                transform: open ? "translateY(0) scale(1)" : "translateY(30px) scale(0.95)",
                opacity: open ? 1 : 0,
                transition: "all 0.5s ease-out",
              }}
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        {/* Footer */}
        <div
          className="pb-6 sm:pb-8 text-center"
          style={{
            transitionDelay: open ? "280ms" : "0ms",
            transform: open ? "translateY(0)" : "translateY(20px)",
            opacity: open ? 1 : 0,
            transition: "all 0.5s ease-out",
          }}
        >
          <div className="w-10 sm:w-12 h-px bg-amber-700/30 mx-auto mb-3 sm:mb-4" />
          <p className="text-white/20 text-[9px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.25em] uppercase">
            © {new Date().getFullYear()} The Lutyens Project
          </p>
        </div>
      </div>
    </>
  );
}
